import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribing(true);
    setMessage('');

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert([{ email }]);

      if (error) {
        if (error.code === '23505') {
          setMessage('Este email ya está suscrito');
        } else {
          throw error;
        }
      } else {
        setMessage('¡Suscripción exitosa! Gracias por unirte.');
        setEmail('');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('Error al suscribirse. Inténtalo de nuevo.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Announcement Bar */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-orange-600 text-white py-2 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap">
          <span className="inline-block px-4 sm:px-8 text-xs sm:text-sm font-medium">
            ✓ Envío GRATIS en 48 horas
          </span>
          <span className="inline-block px-4 sm:px-8 text-xs sm:text-sm font-medium">
            ✓ Obligatorio desde Enero 2026
          </span>
          <span className="inline-block px-4 sm:px-8 text-xs sm:text-sm font-medium">
            ✓ Certificadas por la DGT
          </span>
          <span className="inline-block px-4 sm:px-8 text-xs sm:text-sm font-medium">
            ✓ Conectividad incluida hasta 2038
          </span>
        </div>
      </div>

      <main className="pt-28">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://geobaliza.com/cdn/shop/files/geobaliza-v16-conectada_da468dea-a10c-4dd4-829c-f27168c1c878.jpg?v=1750074183&width=2500" 
              alt="Baliza V16" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative container mx-auto px-4 py-16 sm:py-20 lg:py-32">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-600 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                Obligatorio desde Enero 2026
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
                Baliza V16 Conectada
                <br />
                <span className="text-orange-400">Homologada DGT</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8 leading-relaxed px-4">
                La nueva señal luminosa de emergencia obligatoria en España. Geolocalización en tiempo real, visible a kilómetros de distancia y conectividad incluida hasta 2038.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <a 
                  href="#productos" 
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-orange-600 hover:bg-orange-700 text-white text-sm sm:text-base font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Comprar Ahora
                  <i className="ri-arrow-right-line ml-2 text-lg sm:text-xl"></i>
                </a>
                <a 
                  href="#normativa" 
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm sm:text-base font-semibold rounded-lg transition-all border border-white/20 whitespace-nowrap cursor-pointer"
                >
                  Más Información
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                  <i className="ri-shield-check-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Certificada DGT</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Cumple con la normativa oficial de la Dirección General de Tráfico para uso obligatorio desde enero 2026.
                </p>
              </div>
              <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                  <i className="ri-map-pin-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Geolocalización</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Envía tu ubicación anónima en tiempo real a la DGT 3.0 y comparte la incidencia con otros conductores.
                </p>
              </div>
              <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                  <i className="ri-wifi-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Conectividad Incluida</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  12 años de conectividad ilimitada con Telefónica Tech incluidos. Sin costes adicionales ni suscripciones.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section id="productos" className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
                Baliza V16 Conectada Homologada
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
                La solución oficial certificada por la DGT. Cumple con la normativa obligatoria desde enero 2026.
              </p>
            </div>
            <div className="max-w-2xl mx-auto" data-product-shop>
              {/* Main Product */}
              <div className="bg-white border-2 border-orange-600 rounded-2xl overflow-hidden shadow-2xl">
                <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-orange-50">
                  <img 
                    src="https://geobaliza.com/cdn/shop/files/baliza-v16-geobaliza-iot-01-pack-d.png?v=1765905015&width=600" 
                    alt="Baliza GEOBALIZA IoT V16" 
                    className="w-full h-full object-contain p-8 sm:p-12"
                  />
                  <div className="absolute top-4 sm:top-6 left-4 sm:left-6">
                    <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-600 text-white text-xs sm:text-sm font-bold rounded-full shadow-lg">
                      ⚡ OFERTA ESPECIAL
                    </span>
                  </div>
                  <div className="absolute top-4 sm:top-6 right-4 sm:right-6">
                    <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-600 text-white text-xs sm:text-sm font-bold rounded-full shadow-lg">
                      ✓ CERTIFICADA DGT
                    </span>
                  </div>
                </div>
                <div className="p-6 sm:p-8 lg:p-12">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                    Baliza GEOBALIZA IoT V16
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                    La baliza V16 conectada más completa del mercado. Con geolocalización en tiempo real, conectividad Telefónica incluida hasta 2038 y homologación oficial de la DGT.
                  </p>
                  
                  <div className="flex items-baseline gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
                    <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-600">30,90 €</span>
                    <span className="text-xl sm:text-2xl text-gray-400 line-through">36,35 €</span>
                    <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 text-xs sm:text-sm font-bold rounded-full whitespace-nowrap">
                      -15% OFF
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                    <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <i className="ri-wifi-line text-xl sm:text-2xl text-orange-600 flex-shrink-0"></i>
                      <div>
                        <p className="text-xs text-gray-500">Conectividad</p>
                        <p className="text-sm sm:text-base font-bold text-gray-900">Hasta 2038</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <i className="ri-map-pin-line text-xl sm:text-2xl text-orange-600 flex-shrink-0"></i>
                      <div>
                        <p className="text-xs text-gray-500">Tecnología</p>
                        <p className="text-sm sm:text-base font-bold text-gray-900">Geolocalización</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <i className="ri-shield-check-line text-xl sm:text-2xl text-orange-600 flex-shrink-0"></i>
                      <div>
                        <p className="text-xs text-gray-500">Garantía</p>
                        <p className="text-sm sm:text-base font-bold text-gray-900">3 Años</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <i className="ri-truck-line text-xl sm:text-2xl text-orange-600 flex-shrink-0"></i>
                      <div>
                        <p className="text-xs text-gray-500">Envío</p>
                        <p className="text-sm sm:text-base font-bold text-gray-900">Gratis 48h</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <i className="ri-check-line text-lg sm:text-xl text-green-600 flex-shrink-0"></i>
                      <span className="text-sm sm:text-base text-gray-700">Conectividad Telefónica Tech incluida</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <i className="ri-check-line text-lg sm:text-xl text-green-600 flex-shrink-0"></i>
                      <span className="text-sm sm:text-base text-gray-700">Homologada y certificada por la DGT</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <i className="ri-check-line text-lg sm:text-xl text-green-600 flex-shrink-0"></i>
                      <span className="text-sm sm:text-base text-gray-700">Visible a kilómetros de distancia</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <i className="ri-check-line text-lg sm:text-xl text-green-600 flex-shrink-0"></i>
                      <span className="text-sm sm:text-base text-gray-700">Batería de larga duración</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <i className="ri-check-line text-lg sm:text-xl text-green-600 flex-shrink-0"></i>
                      <span className="text-sm sm:text-base text-gray-700">Fácil instalación magnética</span>
                    </div>
                  </div>

                  <Link
                    to="/producto"
                    className="w-full bg-orange-600 text-white py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center cursor-pointer whitespace-nowrap mb-3 sm:mb-4"
                  >
                    Comprar Ahora
                  </Link>
                  
                  <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500">
                    <i className="ri-secure-payment-line text-base sm:text-lg flex-shrink-0"></i>
                    <span>Pago 100% seguro | Envío gratis en 48h</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-8 sm:mt-12 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <i className="ri-shield-check-line text-xl sm:text-2xl text-orange-600"></i>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-900">Certificada DGT</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <i className="ri-truck-line text-xl sm:text-2xl text-orange-600"></i>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-900">Envío Gratis</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <i className="ri-arrow-go-back-line text-xl sm:text-2xl text-orange-600"></i>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-900">30 Días Devolución</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <i className="ri-customer-service-2-line text-xl sm:text-2xl text-orange-600"></i>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-900">Soporte 24/7</p>
              </div>
            </div>
          </div>
        </section>

        {/* Normativa Section */}
        <section id="normativa" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-orange-50 to-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-600 text-white rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                  Normativa DGT 2026
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Uso Obligatorio desde Enero 2026
                </h2>
                <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                  La Dirección General de Tráfico establece que a partir del <strong>1 de enero de 2026</strong>, todos los vehículos en España deberán llevar una baliza V16 conectada con geolocalización.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-base sm:text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-base font-bold text-gray-900 mb-1">Visibilidad Digital 3.0</h4>
                      <p className="text-sm sm:text-base text-gray-600">Envía tu ubicación anónima a la DGT y paneles de carretera en tiempo real.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-base sm:text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-base font-bold text-gray-900 mb-1">Mayor Seguridad</h4>
                      <p className="text-sm sm:text-base text-gray-600">No necesitas salir del vehículo. Colócala en el techo y actívala desde dentro.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-base sm:text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-base font-bold text-gray-900 mb-1">Sustituye a los Triángulos</h4>
                      <p className="text-sm sm:text-base text-gray-600">La baliza V16 reemplaza oficialmente a los triángulos de emergencia tradicionales.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://geobaliza.com/cdn/shop/files/geobaliza-v16-visibilidad-digital.webp?v=1749052491&width=1200" 
                    alt="Visibilidad Digital DGT 3.0" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="beneficios" className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                ¿Por Qué Elegir Nuestra Baliza?
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
                Calidad, seguridad y cumplimiento normativo garantizado
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-truck-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Envío Gratis</h3>
                <p className="text-sm sm:text-base text-gray-600 px-4">Recibe tu baliza sin coste adicional en toda España.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-shield-star-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Garantía Extendida</h3>
                <p className="text-sm sm:text-base text-gray-600 px-4">Hasta 3 años de garantía oficial del fabricante incluida.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-customer-service-2-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Soporte Técnico</h3>
                <p className="text-sm sm:text-base text-gray-600 px-4">Atención personalizada para resolver todas tus dudas.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-secure-payment-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Pago Seguro</h3>
                <p className="text-sm sm:text-base text-gray-600 px-4">Múltiples métodos de pago con máxima seguridad.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-arrow-go-back-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Devolución Fácil</h3>
                <p className="text-sm sm:text-base text-gray-600 px-4">30 días para devoluciones sin complicaciones.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-verified-badge-line text-2xl sm:text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">100% Certificadas</h3>
                <p className="text-sm sm:text-base text-gray-600 px-4">Todas nuestras balizas están homologadas por la DGT.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-orange-600 to-orange-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 px-4">
              ¿Listo para Cumplir con la Normativa?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto opacity-90 px-4">
              No esperes al último momento. Consigue tu baliza V16 ahora con descuento especial.
            </p>
            <a 
              href="#productos" 
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-orange-600 text-sm sm:text-base font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Comprar Ahora
              <i className="ri-arrow-right-line ml-2 text-lg sm:text-xl"></i>
            </a>
          </div>
        </section>

        {/* Newsletter Section */}
        <section id="contacto" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
                Mantente Informado
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 px-4">
                Suscríbete para recibir ofertas exclusivas y novedades sobre la normativa DGT
              </p>
              
              {message && (
                <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg mx-4 ${message.includes('Error') || message.includes('ya está') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                  <p className="text-sm sm:text-base">{message}</p>
                </div>
              )}

              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-md mx-auto px-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Tu correo electrónico"
                  className="flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm sm:text-base"
                  required
                  disabled={subscribing}
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-orange-600 hover:bg-orange-700 text-white text-sm sm:text-base font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {subscribing ? 'Suscribiendo...' : 'Suscribirse'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
